import {
  DEFAULT_VALUES,
  DEPLOYMENT_KEY_FIELD
} from "@/pages/DeploymentsPage/const"
import { getImageDefaultValues } from "@/pages/DeploymentsPage/helpers/container-image"
import { getShellVariableLine } from "@/pages/DeploymentsPage/helpers/deployment-command"

import type { DeployCommandLine } from "@/pages/DeploymentsPage/helpers/deployment-command.types"
import type { AzureAppPlatform, AzureAppScript } from "./AzureAppsTab.types"

const SECRET_NAME = "kodem-deployment-key"
const APP_ARGS = '-g "$RESOURCE_GROUP" -n "$APP_NAME"'

// The image the launcher reports when the app's identity cannot read it from
// Azure Resource Manager
const AZURE_IMAGE_SETTING = 'KODEM_AZURE_IMAGE="$IMAGE"'

export const getSettingsDefaultValues = ({
  environment = DEFAULT_VALUES.ENVIRONMENT
}: { environment?: string } = {}) => ({
  ...getImageDefaultValues(),
  resourceGroup: DEFAULT_VALUES.RESOURCE_GROUP,
  appName: DEFAULT_VALUES.APP_NAME,
  deploymentKey: DEFAULT_VALUES.DEPLOYMENT_KEY,
  environment
})

const APP_VARIABLE_LINES = {
  resourceGroup: getShellVariableLine("RESOURCE_GROUP", "resourceGroup"),
  appName: getShellVariableLine("APP_NAME", "appName"),
  image: getShellVariableLine("IMAGE", "image")
}

const APP_VARIABLE_GROUPS: (keyof typeof APP_VARIABLE_LINES)[][] = [
  ["resourceGroup"],
  ["appName"],
  ["image"]
]

// Every script line stays short enough to fit the command box: long commands
// put the app arguments on their own line.

// App Service and Functions read the Kodem settings from app settings, then
// run the image; imageFlag is the az flag that sets the container image
const getAppSettingsLines = (
  appCommand: "webapp" | "functionapp",
  imageFlag: string
) =>
  ({
    ...APP_VARIABLE_LINES,
    settings: { text: `az ${appCommand} config appsettings set` },
    settingsApp: { text: APP_ARGS },
    environment: {
      text: '--settings KODEM_ENVIRONMENT="',
      name: "environment",
      suffix: '"'
    },
    deploymentKey: getShellVariableLine(
      "KODEM_DEPLOYMENT_KEY",
      DEPLOYMENT_KEY_FIELD
    ),
    azureImage: { text: AZURE_IMAGE_SETTING },
    container: { text: `az ${appCommand} config container set` },
    containerApp: { text: APP_ARGS },
    containerImage: { text: `${imageFlag} "$IMAGE"` }
  }) satisfies Record<string, DeployCommandLine>

const APP_SETTINGS_GROUPS: (keyof ReturnType<typeof getAppSettingsLines>)[][] =
  [
    ...APP_VARIABLE_GROUPS,
    ["settings", "settingsApp", "environment", "deploymentKey", "azureImage"],
    ["container", "containerApp", "containerImage"]
  ]

// Container Apps expose no resource id to the app, so KODEM_AZURE_RESOURCE_ID
// is required there (KODEM_AZURE_REGION is optional).
const CONTAINER_APPS_LINES = {
  ...APP_VARIABLE_LINES,
  secretComment: {
    text: "# Store the deployment key as a Container Apps secret",
    comment: true
  },
  secret: { text: `az containerapp secret set ${APP_ARGS}` },
  deploymentKey: {
    text: `--secrets ${SECRET_NAME}="`,
    name: DEPLOYMENT_KEY_FIELD,
    suffix: '"'
  },
  appComment: {
    text: "# The app's id and region (KODEM_AZURE_REGION is optional)",
    comment: true
  },
  appId: { text: `APP_ID="$(az containerapp show ${APP_ARGS}` },
  appIdQuery: { text: '--query id -o tsv)"' },
  // location is the display name ("East US 2"); the sensors report the ARM
  // region name ("eastus2")
  region: { text: `REGION="$(az containerapp show ${APP_ARGS}` },
  regionQuery: { text: "--query location -o tsv | tr -d '[:space:]' |" },
  regionName: { text: "tr '[:upper:]' '[:lower:]')\"" },
  updateComment: {
    text: "# Deploy the image with the Kodem settings",
    comment: true
  },
  update: { text: `az containerapp update ${APP_ARGS}` },
  updateImage: { text: '--image "$IMAGE"' },
  environment: {
    text: '--set-env-vars KODEM_ENVIRONMENT="',
    name: "environment",
    suffix: '"'
  },
  deploymentKeyRef: { text: `KODEM_DEPLOYMENT_KEY=secretref:${SECRET_NAME}` },
  azureImage: { text: AZURE_IMAGE_SETTING },
  resourceId: { text: 'KODEM_AZURE_RESOURCE_ID="$APP_ID"' },
  regionSetting: { text: 'KODEM_AZURE_REGION="$REGION"' }
} satisfies Record<string, DeployCommandLine>

const CONTAINER_APPS_GROUPS: (keyof typeof CONTAINER_APPS_LINES)[][] = [
  ...APP_VARIABLE_GROUPS,
  ["secretComment"],
  ["secret", "deploymentKey"],
  ["appComment"],
  ["appId", "appIdQuery"],
  ["region", "regionQuery", "regionName"],
  ["updateComment"],
  [
    "update",
    "updateImage",
    "environment",
    "deploymentKeyRef",
    "azureImage",
    "resourceId",
    "regionSetting"
  ]
]

// The deploy script of each platform; the deployment key input is the
// "deploymentKey" line in every script
export const DEPLOY_SCRIPTS: Record<AzureAppPlatform, AzureAppScript> = {
  appService: {
    lines: getAppSettingsLines("webapp", "--container-image-name"),
    groups: APP_SETTINGS_GROUPS
  },
  functions: {
    lines: getAppSettingsLines("functionapp", "--image"),
    groups: APP_SETTINGS_GROUPS
  },
  containerApps: {
    lines: CONTAINER_APPS_LINES,
    groups: CONTAINER_APPS_GROUPS
  }
}

// Recommended: the app's system-assigned identity lets the launcher read the
// image from Azure Resource Manager (Reader on the app); AcrPull on the
// registry adds the digest. Without it, the launcher uses KODEM_AZURE_IMAGE.
export const getIdentityDefaultValues = () => ({
  key: Date.now().toString(),
  resourceGroup: DEFAULT_VALUES.RESOURCE_GROUP,
  appName: DEFAULT_VALUES.APP_NAME,
  acrName: DEFAULT_VALUES.ACR_NAME
})

// assignFlags: identity assign enables the system-assigned identity by
// default for webapp and functionapp, containerapp needs --system-assigned
const getIdentityLines = (
  appCommand: "webapp" | "functionapp" | "containerapp",
  assignFlags = ""
) =>
  ({
    resourceGroup: APP_VARIABLE_LINES.resourceGroup,
    appName: APP_VARIABLE_LINES.appName,
    acrName: getShellVariableLine("ACR_NAME", "acrName"),
    assign: {
      text: `PRINCIPAL_ID="$(az ${appCommand} identity assign${assignFlags}`
    },
    assignApp: { text: APP_ARGS },
    assignQuery: { text: '--query principalId -o tsv)"' },
    appId: { text: `APP_ID="$(az ${appCommand} show ${APP_ARGS}` },
    appIdQuery: { text: '--query id -o tsv)"' },
    reader: {
      text: `az role assignment create --assignee "$PRINCIPAL_ID" --role Reader`
    },
    readerScope: { text: '--scope "$APP_ID"' },
    acrPullComment: {
      text: "# ACR only: AcrPull lets Kodem read the image digest",
      comment: true
    },
    acrPull: {
      text: `az role assignment create --assignee "$PRINCIPAL_ID" --role AcrPull`
    },
    acrPullScope: {
      text: '--scope "$(az acr show -n "$ACR_NAME" --query id -o tsv)"'
    }
  }) satisfies Record<string, DeployCommandLine>

const IDENTITY_GROUPS: (keyof ReturnType<typeof getIdentityLines>)[][] = [
  ["resourceGroup"],
  ["appName"],
  ["acrName"],
  ["assign", "assignApp", "assignQuery"],
  ["appId", "appIdQuery"],
  ["reader", "readerScope"],
  ["acrPullComment"],
  ["acrPull", "acrPullScope"]
]

export const IDENTITY_SCRIPTS: Record<AzureAppPlatform, AzureAppScript> = {
  appService: { lines: getIdentityLines("webapp"), groups: IDENTITY_GROUPS },
  functions: {
    lines: getIdentityLines("functionapp"),
    groups: IDENTITY_GROUPS
  },
  containerApps: {
    lines: getIdentityLines("containerapp", " --system-assigned"),
    groups: IDENTITY_GROUPS
  }
}
